import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { Decimal } from '~/composables/support/decimal'
import { useLayoutMode } from '~/composables/use-layout-mode'

type PurchaseGaItem = {
    item_id: string
    item_name: string
    coupon: string
    discount: number
    index: number
    item_variant: string
    location_id: string
    price: number
    quantity: number
    kiosk: boolean
    plan_type: string | undefined
    plan_name: string | undefined
    enrollment_type: string | undefined
}

type PurchaseMetaContent = {
    id: string
    quantity: number
    item_name: string
    coupon: string
    discount: number
    item_variant: string
    price: number
}

function emitPurchaseGaAndDataLayer(input: {
    shouldTrackGtag: boolean
    shouldTrackDataLayer: boolean
    transactionId: string
    subtotal: string
    salesTaxExclusive: string
    salesTaxInclusive: string
    currencyFormat: string
    items: PurchaseGaItem[]
}) {
    const eventData = {
        transaction_id: input.transactionId,
        value: Number(input.subtotal),
        tax: Number(input.salesTaxExclusive) + Number(input.salesTaxInclusive),
        currency: input.currencyFormat,
        items: input.items
    }

    if (input.shouldTrackGtag) {
        try {
            // @ts-ignore : ignore gtag not defined
            gtag('event', 'purchase', eventData)
        } catch {
            // no-op
        }
    }

    if (input.shouldTrackDataLayer) {
        try {
            // @ts-ignore : Ignore dataLayer object not defined
            window.dataLayer.push({
                event: 'purchase',
                ...eventData
            })
        } catch {
            // no-op
        }
    }
}

function emitPurchaseMeta(input: {
    transactionId: string
    subtotal: string
    currencyFormat: string
    contents: PurchaseMetaContent[]
}) {
    try {
        // @ts-ignore
        if (!window.fbq) {
            return
        }
    } catch {
        return
    }

    try {
        // @ts-ignore
        fbq(
            'track',
            'Purchase',
            {
                value: Number(input.subtotal),
                currency: input.currencyFormat,
                num_items: input.contents.reduce((sum, content) => {
                    return sum + content.quantity
                }, 0),
                content_ids: input.contents.map(content => content.id),
                contents: input.contents
            },
            {
                eventID: input.transactionId
            }
        )
    } catch {
        // no-op
    }
}

export const AnalyticsCommitCheckoutResultFragment = graphql(`
    fragment AnalyticsCommitCheckoutResultFragment on CommitCheckoutResult {
        invoice {
            referenceNumber
            subTotal
            salesTaxExclusive
            salesTaxInclusive
            facility {
                shortName
                organization {
                    googleAnalyticsTag
                    googleAnalyticsTagEcommerceEvents
                    googleAnalyticsTagKioskEcommerceEvents
                    dataLayerEcommerceEvents
                    dataLayerKioskEcommerceEvents
                    metaPixel
                    metaPixelEcommerceEvents
                    metaPixelKioskEcommerceEvents
                    currencyFormat
                }
            }
            lineItems {
                quantity
                unitPrice
                undiscountedUnitPrice
                promotionCode
                productVariant {
                    analyticsIdentifier
                    variantDisplay
                    product {
                        description
                    }
                }
                contract {
                    ... on PlanContract {
                        planType
                        publicName
                    }
                }
                enrollment {
                    enrollmentType {
                        singular
                    }
                }
            }
        }
    }
`)

export const AnalyticsPurchaseInvoiceFragment = graphql(`
    fragment AnalyticsPurchaseInvoiceFragment on Invoice {
        referenceNumber
        subTotal
        salesTaxExclusive
        salesTaxInclusive
        facility {
            shortName
            organization {
                googleAnalyticsTag
                googleAnalyticsTagEcommerceEvents
                googleAnalyticsTagKioskEcommerceEvents
                dataLayerEcommerceEvents
                dataLayerKioskEcommerceEvents
                metaPixel
                metaPixelEcommerceEvents
                metaPixelKioskEcommerceEvents
                currencyFormat
            }
        }
        lineItems {
            quantity
            unitPrice
            undiscountedUnitPrice
            promotionCode
            productVariant {
                analyticsIdentifier
                variantDisplay
                product {
                    description
                }
            }
            contract {
                ... on PlanContract {
                    planType
                    publicName
                }
            }
            enrollment {
                enrollmentType {
                    singular
                }
            }
        }
    }
`)

function emitPurchaseEventFromInvoiceData(
    invoiceData: {
        referenceNumber: number
        subTotal: string
        salesTaxExclusive: string
        salesTaxInclusive: string
        facility: {
            shortName: string
            organization: {
                googleAnalyticsTag: string
                googleAnalyticsTagEcommerceEvents: boolean
                googleAnalyticsTagKioskEcommerceEvents: boolean
                dataLayerEcommerceEvents: boolean
                dataLayerKioskEcommerceEvents: boolean
                metaPixel: string
                metaPixelEcommerceEvents: boolean
                metaPixelKioskEcommerceEvents: boolean
                currencyFormat: string
            }
        }
        lineItems: {
            quantity: number
            unitPrice: string
            undiscountedUnitPrice: string
            promotionCode: string
            productVariant: {
                analyticsIdentifier: string
                variantDisplay: string
                product: {
                    description: string
                }
            }
            contract:
                | {
                      __typename?: 'CustomContract'
                  }
                | {
                      __typename?: 'PlanContract'
                      planType: string
                      publicName: string
                  }
                | null
            enrollment: {
                enrollmentType: {
                    singular: string
                }
            } | null
        }[]
    },
    inKioskMode: boolean
) {
    const shouldTrackGtag =
        invoiceData.facility.organization.googleAnalyticsTag !== '' &&
        (!inKioskMode ||
            invoiceData.facility.organization
                .googleAnalyticsTagKioskEcommerceEvents) &&
        invoiceData.facility.organization.googleAnalyticsTagEcommerceEvents
    const shouldTrackDataLayer =
        (!inKioskMode ||
            invoiceData.facility.organization.dataLayerKioskEcommerceEvents) &&
        invoiceData.facility.organization.dataLayerEcommerceEvents
    const shouldTrackMeta =
        invoiceData.facility.organization.metaPixel !== '' &&
        (!inKioskMode ||
            invoiceData.facility.organization.metaPixelKioskEcommerceEvents) &&
        invoiceData.facility.organization.metaPixelEcommerceEvents

    if (!shouldTrackGtag && !shouldTrackDataLayer && !shouldTrackMeta) {
        return
    }

    const locationId = invoiceData.facility.shortName
    const gaItems: PurchaseGaItem[] = []
    const metaContents: PurchaseMetaContent[] = []

    invoiceData.lineItems.forEach((lineItem, lineItemIndex) => {
        let planType = undefined
        let planName = undefined
        if (
            lineItem.contract !== null &&
            lineItem.contract.__typename === 'PlanContract'
        ) {
            planType = lineItem.contract.planType
            planName = lineItem.contract.publicName
        }

        gaItems.push({
            item_id: lineItem.productVariant.analyticsIdentifier,
            item_name: lineItem.productVariant.product.description,
            coupon: lineItem.promotionCode,
            discount: Number(
                new Decimal(lineItem.undiscountedUnitPrice)
                    .minus(new Decimal(lineItem.unitPrice))
                    .abs()
                    .toFixed(2)
            ),
            index: lineItemIndex,
            item_variant: lineItem.productVariant.variantDisplay,
            location_id: locationId,
            price: Number(lineItem.unitPrice),
            quantity: lineItem.quantity,
            kiosk: inKioskMode,
            plan_type: planType,
            plan_name: planName,
            enrollment_type: lineItem.enrollment?.enrollmentType.singular
        })

        metaContents.push({
            id: lineItem.productVariant.analyticsIdentifier,
            quantity: lineItem.quantity,
            item_name: lineItem.productVariant.product.description,
            coupon: lineItem.promotionCode,
            discount: Number(
                new Decimal(lineItem.undiscountedUnitPrice)
                    .minus(new Decimal(lineItem.unitPrice))
                    .abs()
                    .toFixed(2)
            ),
            item_variant: lineItem.productVariant.variantDisplay,
            price: Number(lineItem.unitPrice)
        })
    })

    if (shouldTrackGtag || shouldTrackDataLayer) {
        emitPurchaseGaAndDataLayer({
            shouldTrackGtag,
            shouldTrackDataLayer,
            transactionId: `T_${invoiceData.referenceNumber}`,
            subtotal: invoiceData.subTotal,
            salesTaxExclusive: invoiceData.salesTaxExclusive,
            salesTaxInclusive: invoiceData.salesTaxInclusive,
            currencyFormat: invoiceData.facility.organization.currencyFormat,
            items: gaItems
        })
    }

    if (shouldTrackMeta) {
        emitPurchaseMeta({
            transactionId: `${invoiceData.referenceNumber}`,
            subtotal: invoiceData.subTotal,
            currencyFormat: invoiceData.facility.organization.currencyFormat,
            contents: metaContents
        })
    }
}

export function usePurchaseAnalytics() {
    const { isKiosk } = useLayoutMode()

    function emitPurchaseEventFromInvoice(
        invoice: FragmentType<typeof AnalyticsPurchaseInvoiceFragment> | null
    ) {
        if (invoice === null) {
            return
        }

        const invoiceData = getFragmentData(
            AnalyticsPurchaseInvoiceFragment,
            invoice
        )
        emitPurchaseEventFromInvoiceData(invoiceData, isKiosk.value)
    }

    function emitPurchaseEvent(
        commitCheckoutResult: FragmentType<
            typeof AnalyticsCommitCheckoutResultFragment
        >
    ) {
        const commitCheckoutResultData = getFragmentData(
            AnalyticsCommitCheckoutResultFragment,
            commitCheckoutResult
        )

        if (commitCheckoutResultData.invoice === null) {
            return
        }
        emitPurchaseEventFromInvoiceData(
            commitCheckoutResultData.invoice,
            isKiosk.value
        )
    }

    return {
        emitPurchaseEventFromInvoice,
        emitPurchaseEvent
    }
}
